<template>
  <div class="select-drop-down-wrapper">
    <VSearchableSelect
      :modelValue="currentValue"
      @update:modelValue="handleSelection"
      :placeholder="placeholder"
      :disabled="isDisabled"
      fixed-dropdown
      appearance="ghost"
      class="select-field"
    >
      <VOption
        v-for="(option, index) in options"
        :key="option.id ?? option.value ?? index"
        class="select-option"
        :value="String(option.value ?? option.id ?? index)"
        :text="option.title || String(option.value || '')"
        :label="option.selectBoxValueToShow || option.title || String(option.value || '')"
        :title="option.title || String(option.value || '')"
        :disabled="!!option.disabled"
      />
    </VSearchableSelect>
  </div>
</template>

<script>
/* Styles injected into each VOption shadow root to keep its text on one line. */
const OPTION_TRUNCATION_STYLES = `
  .base { overflow: hidden; }
  .text { min-inline-size: 0; }
  .text-primary,
  .text-secondary {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export default {
  emits: ["onSelectOption", "searchTextChanged"],
  props: {
    options: {
      type: Array,
      default: () => []
    },
    selectedValueProp: {
      type: [String, Number, Object],
      default: ""
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: () => "Select Value"
    }
  },
  computed: {
    currentValue() {
      if (!this.selectedValueProp) return '';
      // If selectedValueProp is an object (e.g. from mobileInput), resolve its value
      if (typeof this.selectedValueProp === 'object' && this.selectedValueProp !== null) {
        return String(this.selectedValueProp.value ?? this.selectedValueProp.id ?? '');
      }
      const propStr = String(this.selectedValueProp);
      // Find the matching option to return the same value format as VOption uses
      const matchedOption = this.options.find(opt => 
        String(opt.value ?? opt.id ?? '') === propStr ||
        String(opt.id ?? '') === propStr ||
        String(opt.template_id ?? '') === propStr
      );
      if (matchedOption) {
        return String(matchedOption.value ?? matchedOption.id ?? '');
      }
      return propStr;
    }
  },
  mounted() {
    this.truncateOptionText();
  },
  updated() {
    this.truncateOptionText();
  },
  methods: {
    // Vivid renders the option text inside its own shadow root, so the ellipsis
    // rules cannot be applied from this component's stylesheet. Append them to
    // each option's shadow root instead. Without this the long label keeps its
    // full width and the dropdown panel grows past the select control.
    truncateOptionText() {
      this.$el?.querySelectorAll('.select-option').forEach((option) => {
        if (!option.shadowRoot || option.shadowRoot.querySelector('style[data-truncate-option]')) return;
        const style = document.createElement('style');
        style.setAttribute('data-truncate-option', '');
        style.textContent = OPTION_TRUNCATION_STYLES;
        option.shadowRoot.appendChild(style);
      });
    },
    handleSelection(value) {
      // Find the full option object that matches the selected value
      const selectedOption = this.options.find((option) => {
        const optionVal = String(option.value ?? option.id ?? '');
        return optionVal === value;
      });

      if (selectedOption) {
        this.$emit("onSelectOption", {
          selectedValue: selectedOption.value ?? selectedOption.id ?? value,
          selectedOption: selectedOption
        });
      }
    }
  }
};
</script>

<style scoped>
.select-drop-down-wrapper {
  width: 100%;
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
}

.select-field {
  width: 100%;
}

/* Vivid sizes the dropdown panel to max-content, so a long option label makes
   the panel wider than the select control. Capping the option to the control
   width (exposed by the fixed dropdown) keeps the panel aligned with it. */
.select-option {
  max-inline-size: var(--_searchable-select-fixed-width, 100%);
}
</style>
